import React, { useEffect, useRef } from "react";
import dayjs from "dayjs";
import DatePicker from "antd/lib/date-picker";
import { formatDdMmYyyyTypedInput } from "../../utils/birthDateDdMmYyyyInput";

const DATE_FORMAT = "DD/MM/YYYY";

/**
 * DatePicker לתאריך לידה עם אותה לוגיקת "/" אוטומטית כמו ב-PassengerData (אתר סוכנים).
 */
const AutoFormatBirthDatePicker = ({
  pickerKey,
  title,
  disabled = false,
  returnDate,
  minAge,
  maxAge,
  value,
  onChange,
}) => {
  const datePickerRef = useRef(null);

  const dayjsMinDate = returnDate
    ? dayjs(returnDate, "DD/MM/YYYY").subtract(maxAge, "year")
    : dayjs().subtract(maxAge, "year");
  const dayjsMaxDate = returnDate
    ? dayjs(returnDate, "DD/MM/YYYY").subtract(minAge, "year")
    : dayjs().subtract(minAge, "year");

  const dateValue = value || undefined;

  useEffect(() => {
    if (!datePickerRef.current) return;
    const input = datePickerRef.current.querySelector("input");
    if (!input) return;

    let isFormatting = false;

    const handleKeyPress = (e) => {
      if (!/\d/.test(e.key)) return;
      const el = e.target;
      const currentValue = el.value || "";
      // מתחשב בטקסט מסומן — הקלדה מחליפה אותו, כך שאפשר לערוך תאריך קיים בלי לנקות קודם
      const selStart = el.selectionStart ?? currentValue.length;
      const selEnd = el.selectionEnd ?? currentValue.length;
      const remainingValue = currentValue.slice(0, selStart) + currentValue.slice(selEnd);
      if (remainingValue.replace(/\D/g, "").length >= 8) {
        e.preventDefault();
      }
    };

    // בפוקוס מסמנים את כל הערך הקיים כדי שהקלדה תחליף אותו מיד
    const handleFocus = (e) => {
      const el = e.target;
      if (el.value) {
        setTimeout(() => el.select(), 0);
      }
    };

    const handleInput = (e) => {
      if (isFormatting) return;

      const el = e.target;
      let currentValue = el.value || "";

      if (!currentValue || currentValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return;
      }

      const digitsOnly = currentValue.replace(/\D/g, "");
      if (digitsOnly.length > 0 && digitsOnly.length <= 8) {
        isFormatting = true;
        const formatted = formatDdMmYyyyTypedInput(currentValue);
        const cursorPosition = el.selectionStart || 0;
        const oldValue = currentValue;
        const digitsBeforeCursor = oldValue.slice(0, cursorPosition).replace(/\D/g, "").length;

        el.value = formatted;

        let newPosition = formatted.length;
        let digitCount = 0;
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++;
            if (digitCount >= digitsBeforeCursor) {
              newPosition = i + 1;
              break;
            }
          }
        }

        setTimeout(() => {
          el.setSelectionRange(newPosition, newPosition);
          isFormatting = false;
        }, 0);

        if (formatted.length === 10) {
          const dateObj = dayjs(formatted, DATE_FORMAT);
          if (dateObj.isValid()) {
            setTimeout(() => {
              onChange(formatted);
            }, 50);
          }
        }
      }
    };

    input.addEventListener("keypress", handleKeyPress);
    input.addEventListener("input", handleInput, { passive: false });
    input.addEventListener("focus", handleFocus);

    return () => {
      input.removeEventListener("keypress", handleKeyPress);
      input.removeEventListener("input", handleInput);
      input.removeEventListener("focus", handleFocus);
    };
  }, [pickerKey, onChange]);

  return (
    <div ref={datePickerRef}>
      <DatePicker
        key={pickerKey}
        title={title}
        picker="date"
        disabled={disabled}
        minDate={dayjsMinDate}
        maxDate={dayjsMaxDate}
        defaultValue={
          dayjs(dateValue, DATE_FORMAT).isValid() ? dayjs(dateValue, DATE_FORMAT) : null
        }
        onChange={(dates, dateStrings) => {
          let formattedDate = dateStrings;
          if (dateStrings && typeof dateStrings === "string") {
            const digitsOnly = dateStrings.replace(/\D/g, "");
            if (digitsOnly.length > 0 && digitsOnly.length <= 8 && !dateStrings.includes("/")) {
              formattedDate = formatDdMmYyyyTypedInput(dateStrings);
              if (formattedDate.length === 10) {
                const dateObj = dayjs(formattedDate, DATE_FORMAT);
                if (dateObj.isValid()) {
                  onChange(formattedDate);
                  return;
                }
              }
            }
          }
          onChange(formattedDate);
        }}
        style={{ width: 150 }}
        format={DATE_FORMAT}
      />
    </div>
  );
};

export default AutoFormatBirthDatePicker;
